/**
 * Every Profile event, as one typed union (KTD10). Each is sent once, from the side that
 * knows the fact. A name in both lists would be counted twice. Views are server events: the
 * Worker is what knows whether the viewer is the owner or a bot.
 */
export const SERVER_ACTIONS = [
  // The owner gate.
  'access_denied',
  // R15, as `preview_profile_<action>`. `entry_added` carries `entry_type`; `visibility_changed` carries `from` and `to`.
  'created',
  'entry_added',
  'visibility_changed',
  // Every non-owner, non-bot view; signed out under `profile-anonymous`.
  'viewed',
  // U6: a lookup started (`state`), and how it ended (`state`, `result`, `reason`).
  'verification_attempted',
  'verification_result',
  // Staff testing tools: the page filled with the sample, or emptied. Their own names, so the
  // tool never reads as a teacher adding entries or changing who can see the page.
  'sample_filled',
  'cleared',
] as const;
/**
 * Only the browser knows these happened. Each carries `event_source: 'browser'`.
 * `link_copied` carries `visibility`; `preview_opened` is the owner opening "View as visitor".
 */
export const BROWSER_ACTIONS = ['link_copied', 'preview_opened'] as const;
export type ServerAction = typeof SERVER_ACTIONS[number];
export type BrowserAction = typeof BROWSER_ACTIONS[number];
export type ProfileAction = ServerAction | BrowserAction;
export type EventName = `preview_profile_${ProfileAction}`;
export interface AnalyticsEvent { name: EventName; properties: Record<string, string | number | boolean | string[]> }
export function profileEvent(action: ProfileAction, properties: AnalyticsEvent['properties'] = {}): AnalyticsEvent {
  return { name: `preview_profile_${action}`, properties };
}
/** A browser event carries where it was sent from, so a reader never mistakes it for the server's (KTD10). */
export function browserEvent(action: BrowserAction, properties: AnalyticsEvent['properties'] = {}): AnalyticsEvent {
  return profileEvent(action, { ...properties, event_source: 'browser' });
}
